/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import api from "@/http/api";
import { useSocketStore } from "@/socketStore";
import { useAuthStore } from "@/store";
import { useMutation, useQuery, useInfiniteQuery, useQueryClient, keepPreviousData, type InfiniteData } from "@tanstack/react-query";
import { toast } from "sonner";

const getOrders = (search?: string, page: number = 1, limit: number = 50, status?: string, labels: string[] = []) => {
  const query = new URLSearchParams();
  if (search) query.append('search', search);
  if (status) query.append('status', status);
  // Repeated params, matching the array the controller expects. Sorted so the
  // same selection made in a different order reuses one cache entry.
  for (const label of [...labels].sort()) query.append('labels', label);
  query.append('page', page.toString());
  query.append('limit', limit.toString());
  return api.get(`/order/all?${query.toString()}`);
};

export const useOrder = (search?: string, page: number = 1, limit: number = 50, options?: any, labels: string[] = []) => {
  const labelKey = [...labels].sort();
  return useQuery({
    queryKey: ["all-orders", search, page, limit, labelKey],
    queryFn: async () => {
      return await getOrders(search, page, limit, undefined, labels);
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

// `status` scopes the query to a single Kanban column so each column owns its
// own cursor. Without it every column shares one window over the whole order
// list and sparse statuses stay empty until you page past everything newer.
export const useInfiniteOrder = (search?: string, limit: number = 50, status?: string, options?: any, labels: string[] = []) => {
  const labelKey = [...labels].sort();
  return useInfiniteQuery({
    queryKey: ["all-orders-infinite", search, limit, status, labelKey],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getOrders(search, pageParam as number, limit, status, labels);
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      const { currentPage, totalPages } = lastPage.pagination;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

const updateOrderStatus = (
  order_id: string,
  customer_id: string,
  status: string,
  orderRef: string
) => {
  console.log('status status',status)
  return api.post(`/order/update/${orderRef}/${customer_id}`, {
    status,
    orderRef,
  });
};

export const useOrderStatus = () => {
  const queryClient = useQueryClient()
  const sendNotification = useSocketStore((s) => s.sendNotification);
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: ({
      order_id,
      customer_id,
      status,
      orderRef,
    }: {
      order_id: string;
      customer_id: string;
      status: string;
      orderRef: string;
    }) => updateOrderStatus(order_id, customer_id, status, orderRef),
    onError: () => {
      toast.error("Something went wrong!");
    },
    onSuccess: ({ data }, variables) => {
      toast.success('Order Status changed')
      // "all-orders" does not prefix-match "all-orders-infinite" -- they are
      // separate root keys -- so the Kanban board needs its own line. Without
      // it a drag only lives in KanbanView's local state and snaps back the
      // moment any column loads a page.
      // ponytail: refetches every loaded page of every column. Fine at one
      // page per column; move to a targeted setQueryData on source/target
      // status if boards get deep.
      queryClient.invalidateQueries({ queryKey: ["all-orders"] });
      queryClient.invalidateQueries({ queryKey: ["all-orders-infinite"] });
      sendNotification({
        action_by: user?.role!,
        action_by_id: user?.id.toString()!,
        action_type: "status_updated",
        order_id: variables.orderRef,
        title: `Order status changed`,
        metadata: {
          order_ref: variables.order_id,
          order_id: variables.order_id,
          customer_id: data?.updatedOrder.customer_id.toString(),
          adminName: user?.full_name!,
          job_title: data.updatedOrder.job_title,
          status: variables.status,
        },
      });
    },
  });
};

const getOrderInst = (order_id: string) => {
  return api.get(`/admin/order/instruction/${order_id}`);
};

export const useOrderInst = (order_id: string) => {
  return useQuery({
    queryKey: ["order-instruction", order_id],
    queryFn: async () => {
      return await getOrderInst(order_id);
    },
    enabled: !!order_id,
  });
};

const updateOrder = (
  order_id: string,
  customer_id: string,
  updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string }
) => {
  return api.post(`/order/update/${updates.orderRef}/${customer_id}`, {
    ...updates,
    labels: updates.labels ? JSON.stringify(updates.labels) : undefined,
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      order_id,
      customer_id,
      updates,
    }: {
      order_id: string;
      customer_id: string;
      updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string };
    }) => updateOrder(order_id, customer_id, updates),
    onSuccess: () => {
      // "all-orders" does not prefix-match "all-orders-infinite" -- they are
      // separate root keys -- so the Kanban board needs its own line. Without
      // it a drag only lives in KanbanView's local state and snaps back the
      // moment any column loads a page.
      // ponytail: refetches every loaded page of every column. Fine at one
      // page per column; move to a targeted setQueryData on source/target
      // status if boards get deep.
      queryClient.invalidateQueries({ queryKey: ["all-orders"] });
      queryClient.invalidateQueries({ queryKey: ["all-orders-infinite"] });
      toast.success("Order updated successfully");
    },
    onError: () => {
      toast.error("Failed to update order");
    },
  });
};
